import type { AxiosRequestConfig } from "axios"

import { defineRequest, defineRequestWithParam, instance, request } from "../axios"
import type {
    CreateJourneyPayload,
    ExportJourneyEffectiveUserListPayload,
    JourneyCopyResponse,
    JourneyItem,
    JourneyItemListResponse,
    JourneyListResponse,
    JourneyNodeLogResponse,
    JourneyPerformanceResponse,
    JourneyReviewListResponse,
    JourneyStatusCountResponse,
    JourneyStatusListResponse,
    PatchJourneyPayload,
    ReviewJourneyPayload,
    UpdateJourneyPriorityPayload,
} from "./types"

// 取得 journeys
export const getJourneys = defineRequest<JourneyListResponse>({
    url: "/journeys/",
    method: "get",
})

// 取得 journeys 類別
export const getJourneyStatus = defineRequest<JourneyStatusListResponse>({
    url: "/journeys/status/",
    method: "get",
})

// 取得 journeys 各類別數量
export const getJourneysStatusCount = defineRequest<JourneyStatusCountResponse>({
    url: "/journeys/counts/",
    method: "get",
})

// 取得單個 journey
export const getJourney = defineRequestWithParam<JourneyItem>({
    url: (id: string) => `/journeys/${id}/`,
    method: "get",
})

// 新增 journey
export const createJourney = defineRequest<JourneyItem, CreateJourneyPayload>({
    url: "/journeys/",
    method: "post",
})

// 複製 journey
export const copyJourney = defineRequestWithParam<JourneyCopyResponse>({
    url: (id: string) => `/journeys/${id}/copy/`,
    method: "post",
})

// 編輯 journey
export const updateJourney = defineRequestWithParam<JourneyItem>({
    url: (id: string) => `/journeys/${id}/`,
    method: "put",
})

// 部分更新 journey
export const patchJourney = defineRequestWithParam<JourneyItem, PatchJourneyPayload>({
    url: (id: string) => `/journeys/${id}/`,
    method: "patch",
})

// 刪除 journey
export const deleteJourney = defineRequestWithParam<void>({
    url: (id: string) => `/journeys/${id}/`,
    method: "delete",
})

// 送審 journey
export const submitJourney = defineRequestWithParam<void>({
    url: (id: string) => `/journeys/${id}/submit/`,
    method: "post",
})

// 審核 journey
export const reviewJourney = defineRequestWithParam<void, ReviewJourneyPayload>({
    url: (id: string) => `/journeys/${id}/review/`,
    method: "post",
})

// 取得 journeys 審核人員
export const getJourneysReviewers = defineRequestWithParam<JourneyReviewListResponse>({
    url: (uuid: string) => `/journeys/${uuid}/reviewers/`,
    method: "get",
})

// 取消排程 journey
export const cancelJourney = defineRequestWithParam<void>({
    url: (id: string) => `/journeys/${id}/terminate/`,
    method: "post",
})

// 取得劇本成效
export const getJourneyEffective = defineRequestWithParam<JourneyPerformanceResponse>({
    url: (uuid: string) => `/journeys/${uuid}/analytics/performance/`,
    method: "get",
})

// 取得劇本成效名單
export function getJourneyEffectiveUserList(
    uuid: string,
    nodeShortId: string,
    port: string,
    config: AxiosRequestConfig = {}
) {
    return instance.request<JourneyNodeLogResponse>({
        url: `/journeys/${uuid}/nodes/${nodeShortId}/logs/${port}/`,
        method: "get",
        ...config,
    })
}

// 匯出劇本成效名單
export function exportJourneyEffectiveUserList(
    uuid: string,
    nodeShortId: string,
    port: string,
    config: AxiosRequestConfig<ExportJourneyEffectiveUserListPayload> = {}
) {
    return request<void, ExportJourneyEffectiveUserListPayload>({
        url: `/journeys/${uuid}/nodes/${nodeShortId}/logs/${port}/export/`,
        method: "post",
        ...config,
    })
}

// 取得劇本排序
export const getJourneyPriority = defineRequest<JourneyListResponse>({
    url: "/journeys/priority/",
    method: "get",
})

// 批次更新劇本排序
export const updateJourneyPriority = defineRequest<void, UpdateJourneyPriorityPayload>({
    url: "/journeys/priority/",
    method: "post",
})

// 取得劇本劇本分析大部分資訊
export const getJourneyAnalytics = defineRequestWithParam<void>({
    url: (uuid: string) => `/journeys/${uuid}/analytics/`,
    method: "get",
})

// 取得劇本劇本群組參與分析
export const getJourneyAnalyticsCohort = defineRequestWithParam<void>({
    url: (uuid: string) => `/journeys/${uuid}/analytics/cohort/`,
    method: "get",
})

// 取得劇本有效母數節點列表
export const getJourneySourceNode = defineRequestWithParam<void>({
    url: (uuid: string) => `/journeys/${uuid}/source-node/`,
    method: "get",
})

// 取得母數節點商品分類
export const getJourneyItemList = defineRequest<JourneyItemListResponse>({
    url: "/journeys/item-list/",
    method: "get",
})

// 匯出劇本分析結果
export const exportJourneyAnalytics = defineRequestWithParam<void>({
    url: (uuid: string) => `/journeys/${uuid}/analytics/export/`,
    method: "post",
})
